import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import swal from "sweetalert";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      swal("Error", "Invalid or missing reset token", "error");
      navigate("/forgot-password");
    }
  }, [token, navigate]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      swal("Error", "Passwords do not match", "error");
      return;
    }
    if (password.length < 6) {
      swal("Error", "Password must be at least 6 characters long", "error");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch("https://dairy.server.statify.co.ke/api/users/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reset_token: token, password }),
      });
      const data = await response.json();
      if (response.ok) {
        swal("Success", "Password has been reset successfully", "success");
        navigate("/login");
      } else {
        swal("Error", data.error || "Failed to reset password", "error");
      }
    } catch (err) {
      swal("Error", "Server error during reset", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>Reset Your Password</h2>
          <p style={styles.subtitle}>Create a new secure password for your account</p>
        </div>
        
        <form onSubmit={handleResetPassword} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>New Password</label>
            <input
              type="password"
              placeholder="Enter new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              required
              minLength="6"
            />
          </div>
          
          <div style={styles.inputGroup}>
            <label style={styles.label}>Confirm Password</label>
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              style={styles.input}
              required
              minLength="6"
            />
          </div>

          <button 
            type="submit" 
            disabled={loading}
            style={{
              ...styles.button,
              ...(loading ? styles.buttonDisabled : styles.buttonEnabled)
            }}
          >
            {loading ? (
              <div style={styles.loadingContent}>
                <div style={styles.spinner}></div>
                Resetting Password...
              </div>
            ) : (
              "Reset Password"
            )}
          </button>
        </form>

        <div style={styles.footer}>
          <p style={styles.footerText}>
            Remember your password?{" "}
            <span 
              style={styles.loginLink}
              onClick={() => navigate("/login")}
            >
              Back to Login
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: "20px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  card: {
    background: "white",
    borderRadius: "16px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
    padding: "40px",
    width: "100%",
    maxWidth: "450px",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.2)"
  },
  header: {
    textAlign: "center",
    marginBottom: "30px"
  },
  title: {
    color: "#333",
    fontSize: "28px",
    fontWeight: "700",
    margin: "0 0 8px 0",
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text"
  },
  subtitle: {
    color: "#666",
    fontSize: "14px",
    margin: "0",
    lineHeight: "1.5"
  },
  form: {
    width: "100%"
  },
  inputGroup: {
    marginBottom: "20px"
  },
  label: {
    display: "block",
    marginBottom: "8px",
    color: "#333",
    fontSize: "14px",
    fontWeight: "600"
  },
  input: {
    width: "100%",
    padding: "14px 16px",
    border: "2px solid #e1e5e9",
    borderRadius: "10px",
    fontSize: "16px",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    outline: "none",
    background: "#f8f9fa"
  },
  button: {
    width: "100%",
    padding: "16px",
    border: "none",
    borderRadius: "10px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    marginTop: "10px",
    fontFamily: "inherit"
  },
  buttonEnabled: {
    background: "linear-gradient(135deg, #667eea, #764ba2)",
    color: "white",
    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)"
  },
  buttonDisabled: {
    background: "#ccc",
    color: "#666",
    cursor: "not-allowed",
    boxShadow: "none"
  },
  loadingContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px"
  },
  spinner: {
    width: "16px",
    height: "16px",
    border: "2px solid transparent",
    borderTop: "2px solid currentColor",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },
  footer: {
    marginTop: "25px",
    textAlign: "center"
  },
  footerText: {
    color: "#666",
    fontSize: "14px",
    margin: "0"
  },
  loginLink: {
    color: "#667eea",
    cursor: "pointer",
    fontWeight: "600",
    transition: "color 0.3s ease"
  }
};

// Add CSS for spinner animation
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(`
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`, styleSheet.cssRules.length);

export default ResetPasswordPage;